/**
 * PostHog Client-Side Initialization
 *
 * This file is automatically loaded by Next.js 15.3+ on the client side.
 * It initializes PostHog for analytics, session recording, and feature flags.
 *
 * PostHog is loaded with `opt_out_capturing_by_default: true` and
 * `persistence: "memory"` so that no data is captured and no cookies /
 * localStorage keys are written until the user grants analytics consent
 * via the cookie banner. The banner calls `posthog.opt_in_capturing()`
 * and switches persistence to `"localStorage"` on acceptance.
 *
 * @see https://posthog.com/docs/libraries/next-js
 */
import {
  POSTHOG_MASKED_QUERY_PARAMS,
  scrubSensitiveUrls,
} from "@/lib/analytics/scrub-sensitive-urls";
import { hasValidConsent, readCookieConsent } from "@/lib/utils/cookie-consent";
import {
  allowOptionalPostHogScripts,
  preparePostHogExternalScript,
} from "@/lib/utils/posthog-consent";
import posthog from "posthog-js";
import { onCLS, onFCP, onINP, onLCP, onTTFB } from "web-vitals";

const posthogKey = process.env.NEXT_PUBLIC_POSTHOG_KEY;
const posthogHost =
  process.env.NEXT_PUBLIC_POSTHOG_HOST || "https://us.i.posthog.com";

// Only initialize if we have a valid API key (not a placeholder)
const isValidKey =
  posthogKey &&
  posthogKey.length > 10 &&
  !posthogKey.includes("your-") &&
  !posthogKey.includes("placeholder");

/**
 * Read a value written by `<EnvBootstrap />` (app/env-bootstrap.tsx).
 *
 * The bootstrap server component emits `<meta name="x-app-{env,version}">`
 * tags into `<head>` at request time using the live container env vars —
 * this is the authoritative source for `$environment` after image
 * promotion. Falls back to the build-time inlined `NEXT_PUBLIC_*` value
 * (useful for local `next dev` where ACA secrets aren't present and in
 * the defense-in-depth case where the meta tag is missing for any reason).
 */
function readRuntimeMeta(
  name: string,
  buildTimeFallback: string | undefined,
): string {
  if (typeof document !== "undefined") {
    const meta = document.querySelector<HTMLMetaElement>(
      `meta[name="${name}"]`,
    );
    if (meta?.content) return meta.content;
  }
  return buildTimeFallback ?? "unknown";
}

if (isValidKey) {
  // Read the runtime-bootstrapped environment up front so it can gate
  // init-time options like disable_session_recording. The `loaded`
  // callback reads the same values below for posthog.register(), so
  // both code paths stay in sync.
  const appEnv = readRuntimeMeta("x-app-env", process.env.NEXT_PUBLIC_APP_ENV);
  const appVersion = readRuntimeMeta(
    "x-app-version",
    process.env.NEXT_PUBLIC_APP_VERSION,
  );

  if (hasValidConsent() && readCookieConsent()?.analytics) {
    allowOptionalPostHogScripts(true);
  }

  try {
    posthog.init(posthogKey, {
      api_host: posthogHost,
      defaults: "2025-11-30",
      opt_out_capturing_by_default: true,
      persistence: "memory",
      // Surveys are not used. Logs and exception autocapture download only
      // after analytics consent (see preparePostHogExternalScript).
      disable_surveys: true,
      capture_exceptions: false,
      prepare_external_dependency_script: preparePostHogExternalScript,
      capture_performance: {
        web_vitals: false,
      },
      // A pre-hydration native submit on an auth page used to put
      // credentials in the URL; never let them into URL properties.
      mask_personal_data_properties: true,
      custom_personal_data_properties: POSTHOG_MASKED_QUERY_PARAMS,
      before_send: scrubSensitiveUrls,
      autocapture: {
        url_ignorelist: [
          /^\/signin/,
          /^\/signup/,
          /^\/forgot-password/,
          /^\/reset-password/,
        ],
      },
      // Replay privacy is configured here rather than left to PostHog's
      // project-level defaults, so the guarantees in /cookie-policy are
      // enforced by this bundle. Mark any sensitive surface with
      // `data-ph-mask` to keep its text out of recordings.
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: "[data-ph-mask]",
      },
      // Disable session recording for the `local` deployment environment
      // (covers `next dev` and locally-built release bundles alike). We
      // previously keyed this on `NODE_ENV === "development"`, but a
      // release-mode local build has NODE_ENV=production and would turn
      // recording on, which is noisy and unintended. Gating on the runtime
      // `$environment` label keeps the doc and the code in agreement.
      disable_session_recording: appEnv === "local",
      loaded: (posthog) => {
        posthog.register({
          $environment: appEnv,
          app_version: appVersion,
          platform: "web",
          is_e2e_test: globalThis.window?.__PLAYWRIGHT_TEST__ === true,
        });

        if (process.env.NODE_ENV === "development") {
          posthog.debug();
        }
      },
    });

    // Manually capture Web Vitals and send to PostHog
    // This approach works reliably with Next.js bundling
    const captureWebVital = ({
      name,
      value,
      id,
    }: {
      name: string;
      value: number;
      id: string;
    }) => {
      posthog.capture("$web_vitals", {
        $web_vitals_metric_name: name,
        $web_vitals_metric_value: value,
        $web_vitals_metric_id: id,
      });
    };

    onCLS(captureWebVital);
    onINP(captureWebVital);
    onLCP(captureWebVital);
    onFCP(captureWebVital);
    onTTFB(captureWebVital);
  } catch (error) {
    console.warn("PostHog initialization failed:", error);
  }
}
